import { RoundCheckButton } from "@/components/RoundCheckButton";
import { useCompleteSession, useSession } from "@/hooks/use-shopping-session";
import { useToggleTodo } from "@/hooks/use-todos";
import type { ShoppingSessionItem } from "@/types/shopping-session";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Plus, ShoppingCart, Store } from "lucide-react";
import { useMemo } from "react";

export const Route = createFileRoute("/_app/shopping/$sessionId")({
  component: ShoppingViewPage,
});

function ShoppingViewPage() {
  const navigate = useNavigate();
  const { sessionId } = Route.useParams();
  const { data: session, isLoading } = useSession(sessionId);
  const toggleTodo = useToggleTodo();
  const completeSession = useCompleteSession();

  const groupedItems = useMemo(() => {
    if (!session?.items) return new Map<string, ShoppingSessionItem[]>();
    const map = new Map<string, ShoppingSessionItem[]>();
    for (const item of session.items) {
      const existing = map.get(item.venueName) ?? [];
      map.set(item.venueName, [...existing, item]);
    }
    return map;
  }, [session?.items]);

  const totalItems = session?.items.length ?? 0;
  const checkedItems = session?.items.filter((i) => i.todo.checked).length ?? 0;

  const sessionVenues = useMemo(() => {
    if (!session?.items) return [];
    return [...new Set(session.items.map((i) => i.venueName))];
  }, [session?.items]);

  const handleComplete = async () => {
    if (!session) return;
    await completeSession.mutateAsync(session.id);
    navigate({ to: "/shopping" });
  };

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <p className="text-muted-foreground">読み込み中...</p>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4">
        <p className="text-muted-foreground">セッションが見つかりません</p>
        <button
          type="button"
          onClick={() => navigate({ to: "/shopping" })}
          className="rounded-lg bg-primary px-4 py-2 text-primary-foreground"
        >
          戻る
        </button>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="flex items-center gap-3 border-b px-4 py-3">
        <button
          type="button"
          onClick={() => navigate({ to: "/shopping" })}
          className="rounded-full p-1 text-muted-foreground hover:bg-muted"
          aria-label="戻る"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <ShoppingCart className="h-5 w-5 shrink-0" />
          <h1 className="truncate text-xl font-bold text-foreground">
            {session.name ?? "買い物中"}
          </h1>
        </div>
        <span className="text-sm text-muted-foreground">
          {checkedItems}/{totalItems}
        </span>
        <button
          type="button"
          onClick={() =>
            navigate({
              to: "/shopping/pick",
              search: { venues: sessionVenues, sessionId: session.id },
            })
          }
          className="rounded-full p-1 text-muted-foreground hover:bg-muted"
          aria-label="アイテムを追加"
        >
          <Plus className="h-5 w-5" />
        </button>
      </header>

      <div className="flex-1 overflow-y-auto px-4">
        {[...groupedItems.entries()].map(([venue, items]) => (
          <div key={venue}>
            <h2 className="mt-4 mb-2 flex items-center gap-2 text-sm font-semibold text-muted-foreground">
              <Store className="h-4 w-4" />
              {venue}
            </h2>
            <ul>
              {items.map((item) => (
                <li
                  key={item.todoId}
                  className="flex h-[4.25rem] items-center gap-3 border-b"
                >
                  <RoundCheckButton
                    checked={item.todo.checked}
                    onClick={() =>
                      toggleTodo.mutate({
                        id: item.todoId,
                        checked: item.todo.checked,
                      })
                    }
                  />
                  <div className="min-w-0 flex-1">
                    <p
                      className={`truncate ${
                        item.todo.checked
                          ? "text-muted-foreground line-through"
                          : "text-foreground"
                      }`}
                    >
                      {item.todo.title}
                    </p>
                    {item.todo.quantity && (
                      <span className="text-sm text-muted-foreground">
                        {item.todo.quantity}
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="border-t px-4 py-3">
        <button
          type="button"
          onClick={handleComplete}
          disabled={completeSession.isPending}
          className="w-full rounded-full bg-primary py-3 text-base font-medium text-primary-foreground disabled:opacity-50"
        >
          {completeSession.isPending ? "完了中..." : "買い物完了"}
        </button>
      </div>
    </div>
  );
}
